import {
    CONTENT_SOURCE,
    CONTENT_STATUS,
    CONTENT_TARGET,
    CONTENT_TYPE,
    MAX_FILE_BYTES,
    STATUS_KEY,
    TYPE_KEY,
} from "./constants";

export const emptyDraft = () => ({
    name: "",
    description: "",
    source: CONTENT_SOURCE.FILE,
    targetScope: CONTENT_TARGET.GENERAL,
    surveyId: null,
    itemTypeId: null,
    itemId: null,
    url: "",
    file: null,
    fileName: "",
    contentType: CONTENT_TYPE.VIDEO,
});

export const contentToDraft = (content) => ({
    name: content?.name || "",
    description: content?.description || "",
    source: content?.source || detectSourceFromUrl(content?.url) || CONTENT_SOURCE.FILE,
    targetScope: content?.target_scope || CONTENT_TARGET.GENERAL,
    surveyId: content?.survey || null,
    itemTypeId: content?.item_type || null,
    itemId: firstItemId(content),
    url: content?.url || "",
    file: null,
    fileName: fileNameFromUrl(content?.file),
    contentType: Number(content?.content_type) || CONTENT_TYPE.VIDEO,
});

export const firstItemId = (content) => {
    const items = content?.item;
    if (Array.isArray(items) && items.length) {
        return items[0]?.id ?? items[0];
    }
    return content?.item_id || null;
};

export const fileNameFromUrl = (value) => {
    if (!value || typeof value !== "string") return "";
    try {
        const path = new URL(value, window.location.origin).pathname;
        return decodeURIComponent(path.split("/").pop() || "");
    } catch {
        return value.split("/").pop() || "";
    }
};

export const detectSourceFromUrl = (url) => {
    if (!url) return CONTENT_SOURCE.FILE;
    const host = (() => {
        try {
            return new URL(url).hostname.toLowerCase();
        } catch {
            return "";
        }
    })();
    if (host.includes("youtube.com") || host.includes("youtu.be")) {
        return CONTENT_SOURCE.YOUTUBE;
    }
    if (host.includes("aparat.com")) {
        return CONTENT_SOURCE.APARAT;
    }
    return CONTENT_SOURCE.LINK;
};

export const youtubeVideoId = (url) => {
    if (!url) return "";
    try {
        const parsed = new URL(url);
        const host = parsed.hostname.toLowerCase();
        if (host.includes("youtu.be")) {
            return parsed.pathname.replace(/^\//, "").split("/")[0];
        }
        if (parsed.pathname.startsWith("/embed/")) {
            return parsed.pathname.split("/embed/")[1].split("/")[0];
        }
        if (parsed.pathname.startsWith("/shorts/")) {
            return parsed.pathname.split("/shorts/")[1].split("/")[0];
        }
        return parsed.searchParams.get("v") || "";
    } catch {
        return "";
    }
};

export const aparatVideoHash = (url) => {
    if (!url) return "";
    try {
        const parts = new URL(url).pathname.split("/").filter(Boolean);
        const hashIndex = parts.indexOf("videohash");
        if (hashIndex >= 0) return parts[hashIndex + 1] || "";
        if (parts[0] === "v") return parts[1] || "";
        return parts[parts.length - 1] || "";
    } catch {
        return "";
    }
};

export const embedUrlFor = (content, draft) => {
    const source = draft?.source || content?.source;
    const url = draft?.url || content?.url || "";
    if (content?.embed_url && !draft?.url) return content.embed_url;
    if (source === CONTENT_SOURCE.YOUTUBE) {
        const id = youtubeVideoId(url);
        return id ? `https://www.youtube.com/embed/${id}` : "";
    }
    if (source === CONTENT_SOURCE.APARAT) {
        const hash = aparatVideoHash(url);
        return hash
            ? `https://www.aparat.com/video/video/embed/videohash/${hash}/vt/frame`
            : "";
    }
    return url;
};

export const thumbnailUrlFor = (content, draft) => {
    if (content?.thumbnail_url && !draft?.url) return content.thumbnail_url;
    const source = draft?.source || content?.source;
    const url = draft?.url || content?.url || "";
    if (source === CONTENT_SOURCE.YOUTUBE) {
        const id = youtubeVideoId(url);
        return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
    }
    if (content?.content_type === CONTENT_TYPE.IMAGE && content?.file) {
        return content.file;
    }
    return "";
};

export const statusKey = (status) => STATUS_KEY[Number(status)] || "pending";

export const typeKey = (type) => TYPE_KEY[Number(type)] || "video";

export const formatCount = (value, locale) =>
    Number(value || 0).toLocaleString(locale);

export const formatDateTime = (value, locale) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    try {
        return new Intl.DateTimeFormat(locale, {
            dateStyle: "medium",
            timeStyle: "short",
        }).format(date);
    } catch {
        return date.toLocaleString(locale);
    }
};

export const inferTypeFromFile = (file) => {
    const kind = file?.type?.split("/")[0];
    if (kind === "audio") return CONTENT_TYPE.AUDIO;
    if (kind === "image") return CONTENT_TYPE.IMAGE;
    return CONTENT_TYPE.VIDEO;
};

export const validateFile = (file, t) => {
    if (!file) return t("contentStudio.errors.fileRequired");
    const kind = file.type?.split("/")[0];
    if (!["audio", "video", "image"].includes(kind)) {
        return t("contentStudio.errors.fileType");
    }
    if (file.size > MAX_FILE_BYTES) {
        return t("contentStudio.errors.fileSize");
    }
    return "";
};

export const draftIssues = (draft, t, { isEdit } = {}) => {
    const issues = {};
    if (!draft.name.trim()) issues.name = t("contentStudio.errors.title");
    if (draft.source === CONTENT_SOURCE.FILE) {
        if (!draft.file && !isEdit) {
            issues.file = t("contentStudio.errors.fileRequired");
        }
    } else if (!draft.url.trim()) {
        issues.url = t("contentStudio.errors.linkRequired");
    } else if (!/^https?:\/\//i.test(draft.url.trim())) {
        issues.url = t("contentStudio.errors.linkInvalid");
    } else if (
        draft.source === CONTENT_SOURCE.YOUTUBE &&
        !youtubeVideoId(draft.url)
    ) {
        issues.url = t("contentStudio.errors.youtube");
    } else if (
        draft.source === CONTENT_SOURCE.APARAT &&
        !aparatVideoHash(draft.url)
    ) {
        issues.url = t("contentStudio.errors.aparat");
    }
    if (draft.targetScope === CONTENT_TARGET.SURVEY && !draft.surveyId) {
        issues.surveyId = t("contentStudio.errors.survey");
    }
    if (draft.targetScope === CONTENT_TARGET.ITEM_TYPE && !draft.itemTypeId) {
        issues.itemTypeId = t("contentStudio.errors.itemType");
    }
    if (draft.targetScope === CONTENT_TARGET.ITEM && !draft.itemId) {
        issues.itemId = t("contentStudio.errors.item");
    }
    return issues;
};

export const toSelectOptions = (rows, labelKey = "name") =>
    (Array.isArray(rows) ? rows : rows?.results || []).map((row) => ({
        value: row.id,
        label: row[labelKey] || row.name || row.title,
    }));

export const targetLabel = (content, t) => {
    const scope = content?.target_scope || CONTENT_TARGET.GENERAL;
    if (scope === CONTENT_TARGET.SURVEY) {
        return content.survey_title || t("contentStudio.target.survey");
    }
    if (scope === CONTENT_TARGET.ITEM_TYPE) {
        return content.item_type_name || t("contentStudio.target.itemType");
    }
    if (scope === CONTENT_TARGET.ITEM) {
        const item = Array.isArray(content.item) ? content.item[0] : content.item;
        return item?.name || t("contentStudio.target.item");
    }
    return t("contentStudio.target.general");
};

export const monthLabel = (year, month, locale) => {
    try {
        return new Intl.DateTimeFormat(locale, {
            month: "short",
            year: "numeric",
        }).format(new Date(year, month - 1, 1));
    } catch {
        return `${year}/${month}`;
    }
};
